"use client";

import { Card, CardBody, Checkbox } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ACHIEVEMENTS,
  AchievementKey,
  getAchievementChipClasses,
} from "@/lib/achievements";
import { TournamentStatistics, TournamentTeamStats } from "@/types";

interface Props {
  stats: TournamentStatistics;
  userTeamId?: string | null;
}

type SortKey =
  | "teamName"
  | "currentLevel"
  | "startingLevel"
  | "levelDifference"
  | "matchesPlayed"
  | "wins"
  | "losses"
  | "winRate"
  | "setsWon"
  | "gamesWon"
  | "gamesLost";

type SortDir = "asc" | "desc";

// Numeric columns default to descending (best/most first); text and level
// columns default to ascending so the top of the tower appears first.
const ASC_DEFAULT_KEYS: ReadonlySet<SortKey> = new Set([
  "teamName",
  "currentLevel",
  "startingLevel",
]);

export default function TournamentStatisticsClient({
  stats,
  userTeamId,
}: Props) {
  const t = useTranslations("tournamentStatistics");
  const [excludeWalkover, setExcludeWalkover] = useState(false);

  const hasAnyTeams = stats.teamStats.length > 0;

  return (
    <div className="w-full flex flex-col gap-6">
      <section aria-label={t("teamTable.sectionLabel")}>
        {hasAnyTeams ? (
          <div className="flex flex-col gap-3">
            <div className="flex justify-end">
              <Checkbox
                isSelected={excludeWalkover}
                onValueChange={setExcludeWalkover}
                classNames={{ label: "text-sm" }}
              >
                {t("teamTable.excludeWalkover")}
              </Checkbox>
            </div>
            <TeamStatsTable
              rows={
                excludeWalkover
                  ? stats.teamStatsExcludingWalkover
                  : stats.teamStats
              }
              userTeamId={userTeamId ?? null}
            />
          </div>
        ) : (
          <Card>
            <CardBody className="py-10 text-center text-default-500">
              {t("teamTable.empty")}
            </CardBody>
          </Card>
        )}
      </section>

      <section
        aria-label={t("summary.sectionLabel")}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3"
      >
        <SummaryCard
          achievementKey="mostChallenged"
          label={t("summary.mostChallenged")}
          value={stats.mostChallenged?.teamName ?? t("summary.none")}
          sub={
            stats.mostChallenged
              ? t("summary.timesChallenged", {
                  count: stats.mostChallenged.count,
                })
              : undefined
          }
        />
        <SummaryCard
          achievementKey="topChallenger"
          label={t("summary.topChallenger")}
          value={stats.topChallenger?.teamName ?? t("summary.none")}
          sub={
            stats.topChallenger
              ? t("summary.challengesSent", {
                  count: stats.topChallenger.count,
                })
              : undefined
          }
        />
        <SummaryCard
          achievementKey="mostDefended"
          label={t("summary.mostDefended")}
          value={stats.mostDefended?.teamName ?? t("summary.none")}
          sub={
            stats.mostDefended
              ? t("summary.defendsWon", { count: stats.mostDefended.count })
              : undefined
          }
        />
        <SummaryCard
          achievementKey="mostOwnChallengeWon"
          label={t("summary.mostOwnChallengeWon")}
          value={stats.mostOwnChallengeWon?.teamName ?? t("summary.none")}
          sub={
            stats.mostOwnChallengeWon
              ? t("summary.ownChallengesWon", {
                  count: stats.mostOwnChallengeWon.count,
                })
              : undefined
          }
        />
        <SummaryCard
          achievementKey="topBraveheartDecliner"
          label={t("summary.braveheartDeclines")}
          value={
            stats.topBraveheartDecliner?.teamName ?? t("summary.none")
          }
          sub={t("summary.declinesCount", {
            count: stats.topBraveheartDecliner?.count ?? 0,
            total: stats.braveheartDeclineCount,
          })}
        />
        <SummaryCard
          achievementKey="mostCardsUsed"
          label={t("summary.mostCardsUsed")}
          value={stats.mostCardsUsed?.teamName ?? t("summary.none")}
          sub={
            stats.mostCardsUsed
              ? t("summary.cardsUsed", { count: stats.mostCardsUsed.count })
              : undefined
          }
        />
        <SummaryCard
          achievementKey="mostTightWins"
          label={t("summary.mostTightWins")}
          value={stats.mostTightWins?.teamName ?? t("summary.none")}
          sub={
            stats.mostTightWins
              ? t("summary.tightWinsCount", {
                  count: stats.mostTightWins.count,
                })
              : undefined
          }
        />
        <SummaryCard
          achievementKey="mostGamesPlayed"
          label={t("summary.mostGamesPlayed")}
          value={stats.mostGamesPlayed?.teamName ?? t("summary.none")}
          sub={
            stats.mostGamesPlayed
              ? t("summary.gamesPlayedCount", {
                  count: stats.mostGamesPlayed.count,
                })
              : undefined
          }
        />
        <SummaryCard
          achievementKey="mostMatchesPlayed"
          label={t("summary.mostMatchesPlayed")}
          value={stats.mostMatchesPlayed?.teamName ?? t("summary.none")}
          sub={
            stats.mostMatchesPlayed
              ? t("summary.matchesPlayedCount", {
                  count: stats.mostMatchesPlayed.count,
                })
              : undefined
          }
        />
      </section>
    </div>
  );
}

function SummaryCard({
  achievementKey,
  label,
  value,
  sub,
}: {
  achievementKey: AchievementKey;
  label: string;
  value: string;
  sub?: string;
}) {
  const chip = getAchievementChipClasses(achievementKey);
  const icon = ACHIEVEMENTS[achievementKey].icon;
  return (
    <Card shadow="sm" className="h-full">
      <CardBody className="flex flex-col gap-3 p-4">
        <div className="flex items-start gap-2.5 min-h-[2.5rem]">
          <div
            className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${chip.bg} ${chip.text}`}
          >
            <Icon icon={icon} width={20} height={20} aria-hidden />
          </div>
          <span className="text-[0.7rem] font-semibold uppercase tracking-wider text-default-500 leading-tight pt-1 line-clamp-2">
            {label}
          </span>
        </div>
        <div className="flex flex-col gap-0.5 mt-auto min-w-0">
          <span
            className="truncate text-xl font-semibold text-foreground leading-tight"
            title={value}
          >
            {value}
          </span>
          <span className="text-xs text-default-500 truncate min-h-[1rem]">
            {sub ?? " "}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}

function TeamStatsTable({
  rows,
  userTeamId,
}: {
  rows: TournamentTeamStats[];
  userTeamId: string | null;
}) {
  const t = useTranslations("tournamentStatistics.columns");
  const [sortKey, setSortKey] = useState<SortKey>("currentLevel");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(ASC_DEFAULT_KEYS.has(key) ? "asc" : "desc");
    }
  };

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let cmp: number;
      if (sortKey === "currentLevel") {
        // Composite ranking: tower level → W-L diff → set diff → game diff.
        // Unplaced teams (level 0) sort to the bottom.
        const levelA = a.currentLevel || Number.POSITIVE_INFINITY;
        const levelB = b.currentLevel || Number.POSITIVE_INFINITY;
        cmp =
          levelA - levelB ||
          b.wins - b.losses - (a.wins - a.losses) ||
          b.setDifference - a.setDifference ||
          b.gamesWon - b.gamesLost - (a.gamesWon - a.gamesLost);
      } else {
        const va = a[sortKey];
        const vb = b[sortKey];
        if (typeof va === "number" && typeof vb === "number") {
          cmp = va - vb;
        } else {
          cmp = String(va).localeCompare(String(vb), undefined, {
            sensitivity: "base",
          });
        }
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const renderHeaderRow = useCallback(
    () => (
      <tr>
        <th
          scope="col"
          aria-label={t("rank")}
          className="bg-default-100 px-3 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-center align-bottom border-b border-default-200 text-default-600 w-10"
        >
          {t("rank")}
        </th>
        <SortableTh
          sortKey="teamName"
          activeKey={sortKey}
          dir={sortDir}
          onSort={toggleSort}
          align="left"
          widthClass="w-full"
          sticky="corner"
        >
          {t("team")}
        </SortableTh>
        <SortableTh sortKey="currentLevel" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("currentLevel")}
        </SortableTh>
        <SortableTh sortKey="startingLevel" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("startingLevel")}
        </SortableTh>
        <SortableTh sortKey="levelDifference" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("levelDifference")}
        </SortableTh>
        <SortableTh sortKey="matchesPlayed" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("matches")}
        </SortableTh>
        <SortableTh sortKey="wins" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("wins")}
        </SortableTh>
        <SortableTh sortKey="losses" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("losses")}
        </SortableTh>
        <SortableTh sortKey="winRate" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("winRate")}
        </SortableTh>
        <SortableTh sortKey="setsWon" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("setsWon")}
        </SortableTh>
        <SortableTh sortKey="gamesWon" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("gamesWon")}
        </SortableTh>
        <SortableTh sortKey="gamesLost" activeKey={sortKey} dir={sortDir} onSort={toggleSort}>
          {t("gamesLost")}
        </SortableTh>
      </tr>
    ),
    [sortKey, sortDir, t],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const floatOuterRef = useRef<HTMLDivElement>(null);
  const [floatState, setFloatState] = useState<{
    top: number;
    left: number;
    width: number;
    tableWidth: number;
    widths: number[];
  } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    const thead = theadRef.current;
    if (!container || !thead) return;

    let raf = 0;
    const measure = () => {
      const cRect = container.getBoundingClientRect();
      const tRect = thead.getBoundingClientRect();
      const navVar = getComputedStyle(document.documentElement)
        .getPropertyValue("--tournament-nav-h")
        .trim();
      const navH = parseFloat(navVar) || (window.innerWidth >= 768 ? 54 : 50);
      const siteH = window.innerWidth >= 768 ? 76 : 72;
      const offset = siteH + navH;
      const theadH = tRect.height;

      const shouldFloat =
        tRect.top < offset && cRect.bottom > offset + theadH;

      if (shouldFloat) {
        const cells = Array.from(thead.querySelectorAll("tr > th")) as HTMLElement[];
        const widths = cells.map((c) => c.getBoundingClientRect().width);
        const tableEl = thead.parentElement as HTMLTableElement | null;
        const tableWidth = tableEl?.getBoundingClientRect().width ?? cRect.width;
        setFloatState({
          top: offset,
          left: cRect.left,
          width: cRect.width,
          tableWidth,
          widths,
        });
        if (floatOuterRef.current) {
          floatOuterRef.current.scrollLeft = container.scrollLeft;
        }
      } else {
        setFloatState((prev) => (prev ? null : prev));
      }
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    const syncScrollLeft = () => {
      if (floatOuterRef.current) {
        floatOuterRef.current.scrollLeft = container.scrollLeft;
      }
    };

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    container.addEventListener("scroll", syncScrollLeft, { passive: true });
    const ro = new ResizeObserver(schedule);
    ro.observe(container);
    measure();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      container.removeEventListener("scroll", syncScrollLeft);
      ro.disconnect();
    };
  }, []);

  return (
    <>
    <div
      ref={containerRef}
      className="w-full overflow-x-auto rounded-lg border border-default-200"
    >
      <table className="w-full min-w-[680px] text-sm border-separate border-spacing-0">
        <thead ref={theadRef} className="text-default-700">
          {renderHeaderRow()}
        </thead>
        <tbody>
          {sortedRows.map((row, i) => {
            const isUserRow = userTeamId !== null && row.teamId === userTeamId;
            const rowBg = isUserRow
              ? "bg-primary-900"
              : i % 2 === 0
                ? "bg-background"
                : "bg-default-50";
            return (
              <tr key={row.teamId}>
                <Td
                  className={`${rowBg} tabular-nums text-default-500 w-10`}
                >
                  {i + 1}.
                </Td>
                <Td
                  align="left"
                  className={`sticky left-0 z-10 ${rowBg}`}
                >
                  <span
                    className={`block truncate ${isUserRow ? "font-semibold text-primary-300" : "font-medium text-foreground"}`}
                    title={row.teamName}
                  >
                    {row.teamName}
                  </span>
                </Td>
                <Td className={`${rowBg} tabular-nums`}>
                  <span className="inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-md bg-default-200/70 px-1.5 text-sm font-semibold text-foreground tabular-nums">
                    {row.currentLevel || "—"}
                  </span>
                </Td>
                <Td className={`${rowBg} text-default-500 tabular-nums`}>
                  {row.startingLevel || "—"}
                </Td>
                <Td className={`${rowBg} tabular-nums`}>
                  {row.startingLevel ? (
                    <DiffValue value={row.levelDifference} />
                  ) : (
                    "—"
                  )}
                </Td>
                <Td className={`${rowBg} tabular-nums`}>
                  {row.matchesPlayed}
                </Td>
                <Td className={`${rowBg} tabular-nums text-success-600 dark:text-success`}>
                  <WinLossValue value={row.wins} walkoverCount={row.walkoverWins} />
                </Td>
                <Td className={`${rowBg} tabular-nums text-danger-600 dark:text-danger`}>
                  <WinLossValue value={row.losses} walkoverCount={row.walkoverLosses} />
                </Td>
                <Td className={`${rowBg} tabular-nums font-medium`}>
                  {row.matchesPlayed > 0 ? `${row.winRate}%` : "—"}
                </Td>
                <Td className={`${rowBg} tabular-nums`}>{row.setsWon}</Td>
                <Td className={`${rowBg} tabular-nums text-default-600`}>
                  {row.gamesWon}
                </Td>
                <Td className={`${rowBg} tabular-nums text-default-600`}>
                  {row.gamesLost}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {floatState && (
      <div
        ref={floatOuterRef}
        aria-hidden
        style={{
          position: "fixed",
          top: floatState.top,
          left: floatState.left,
          width: floatState.width,
          zIndex: 30,
          overflowX: "hidden",
          overflowY: "hidden",
        }}
      >
        <table
          className="text-sm border-separate border-spacing-0"
          style={{ width: floatState.tableWidth }}
        >
          <colgroup>
            {floatState.widths.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>
          <thead className="text-default-700">{renderHeaderRow()}</thead>
        </table>
      </div>
    )}
    </>
  );
}

function SortableTh({
  children,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = "center",
  widthClass,
  sticky,
}: {
  children: React.ReactNode;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  align?: "left" | "center";
  widthClass?: string;
  sticky?: "top" | "corner";
}) {
  const isActive = activeKey === sortKey;
  const ariaSort: "ascending" | "descending" | "none" = isActive
    ? dir === "asc"
      ? "ascending"
      : "descending"
    : "none";

  const stickyClass = sticky === "corner" ? "sticky left-0 z-10" : "";

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`bg-default-100 px-0 py-0 text-[0.7rem] font-semibold uppercase tracking-wider align-bottom border-b border-default-200 ${stickyClass} ${
        widthClass ?? ""
      } ${align === "left" ? "text-left" : "text-center"}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`flex min-h-[44px] w-full items-center gap-1 px-3 py-2.5 transition-colors hover:bg-default-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${
          align === "left" ? "justify-start" : "justify-center"
        } ${isActive ? "text-foreground" : "text-default-600"}`}
      >
        <span>{children}</span>
        <Icon
          icon={
            isActive
              ? dir === "asc"
                ? "lucide:arrow-up"
                : "lucide:arrow-down"
              : "lucide:chevrons-up-down"
          }
          width={12}
          height={12}
          aria-hidden
          className={isActive ? "opacity-100" : "opacity-30"}
        />
      </button>
    </th>
  );
}

function Td({
  children,
  align = "center",
  className = "",
}: {
  children: React.ReactNode;
  align?: "left" | "center";
  className?: string;
}) {
  return (
    <td
      className={`px-3 py-2.5 border-b border-default-100 ${align === "left" ? "text-left" : "text-center"} ${className}`}
    >
      {children}
    </td>
  );
}

function DiffValue({ value }: { value: number }) {
  if (value > 0) {
    return (
      <span className="font-medium text-success-600 dark:text-success">
        +{value}
      </span>
    );
  }
  if (value < 0) {
    return (
      <span className="font-medium text-danger-600 dark:text-danger">
        {value}
      </span>
    );
  }
  return <span className="text-default-500">0</span>;
}

// e.g. "4" or "4 (1)" — the parenthetical is the subset decided by walkover.
function WinLossValue({
  value,
  walkoverCount,
}: {
  value: number;
  walkoverCount: number;
}) {
  return (
    <>
      {value}
      {walkoverCount > 0 && (
        <span className="text-default-500"> ({walkoverCount})</span>
      )}
    </>
  );
}
